import { CellClassParams, ColDef } from 'ag-grid-community';

const centerAligned = { headerClass: 'ag-center-header', cellClass: 'ag-center-cell' };
const centerHeaderRightCell = { headerClass: 'ag-center-header', cellClass: 'ag-right-cell' };

export const HIGHLIGHT_THRESHOLDS = {
    DIFF_USD: 200000,
    CHANGE_PERCENT: 5
};

export type HighlightLevel = 'red' | 'yellow' | 'none';

export const getRowHighlightLevel = (data: any): HighlightLevel => {
    const diffUsd = data?.paymentDiffUsd;
    const changePercent = data?.paymentDeltaPercentageUsd;

    const diffUsdExceedsThreshold = diffUsd != null && Math.abs(diffUsd) >= HIGHLIGHT_THRESHOLDS.DIFF_USD;
    const changePercentExceedsThreshold =
        changePercent != null && Math.abs(changePercent) >= HIGHLIGHT_THRESHOLDS.CHANGE_PERCENT;

    if (diffUsdExceedsThreshold && changePercentExceedsThreshold) return 'red';
    if (diffUsdExceedsThreshold || changePercentExceedsThreshold) return 'yellow';
    return 'none';
};

const getDiffUsdCellClass = (params: CellClassParams): string => {
    const diffUsd = params.data?.paymentDiffUsd;
    const changePercent = params.data?.paymentDeltaPercentageUsd;
    const diffExceeds = diffUsd != null && Math.abs(diffUsd) >= HIGHLIGHT_THRESHOLDS.DIFF_USD;
    const changeExceeds = changePercent != null && Math.abs(changePercent) >= HIGHLIGHT_THRESHOLDS.CHANGE_PERCENT;

    if (diffExceeds && changeExceeds) return 'mom-red-highlight-cell';
    if (diffExceeds) return 'mom-yellow-highlight-cell';
    return '';
};

const getChangePercentCellClass = (params: CellClassParams): string => {
    const diffUsd = params.data?.paymentDiffUsd;
    const changePercent = params.data?.paymentDeltaPercentageUsd;
    const diffExceeds = diffUsd != null && Math.abs(diffUsd) >= HIGHLIGHT_THRESHOLDS.DIFF_USD;
    const changeExceeds = changePercent != null && Math.abs(changePercent) >= HIGHLIGHT_THRESHOLDS.CHANGE_PERCENT;

    if (diffExceeds && changeExceeds) return 'mom-red-highlight-cell';
    if (changeExceeds) return 'mom-yellow-highlight-cell';
    return '';
};

const formatNum = (val: number | null | undefined): string => {
    if (val == null) return '';
    const formatted = Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return val < 0 ? `(${formatted})` : formatted;
};

const formatPct = (val: number | null | undefined, fallback = ''): string => {
    if (val == null) return fallback;
    return val < 0 ? `(${Math.abs(val).toFixed(2)}%)` : `${val.toFixed(2)}%`;
};

const absComparator = (a: number | null, b: number | null): number => {
    const absA = a != null ? Math.abs(a) : 0;
    const absB = b != null ? Math.abs(b) : 0;
    return absA - absB;
};

export const monthOverMonthPaymentsColDefs: ColDef[] = [
    {
        field: 'serviceName',
        headerName: 'Service',
        filter: 'agSetColumnFilter',
        sortable: true,
        ...centerAligned
    },
    {
        field: 'territoryCode',
        headerName: 'Territory',
        filter: 'agSetColumnFilter',
        sortable: true,
        ...centerAligned
    },
    {
        field: 'currency',
        headerName: 'Currency',
        filter: 'agSetColumnFilter',
        sortable: true,
        ...centerAligned
    },
    {
        field: 'paymentAmountPrev',
        headerName: 'Previous Amount',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        sortable: true,
        valueFormatter: (params) => formatNum(params.value),
        ...centerHeaderRightCell
    },
    {
        field: 'paymentAmountCurr',
        headerName: 'Current Amount',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        sortable: true,
        valueFormatter: (params) => formatNum(params.value),
        ...centerHeaderRightCell
    },
    {
        field: 'paymentDiff',
        headerName: 'Difference',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        sortable: true,
        comparator: absComparator,
        valueFormatter: (params) => formatNum(params.value),
        ...centerHeaderRightCell
    },
    {
        field: 'paymentDeltaPercentage',
        headerName: 'Change %',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        sortable: true,
        comparator: absComparator,
        valueFormatter: (params) => formatPct(params.value),
        ...centerHeaderRightCell
    },
    {
        field: 'paymentDiffUsd',
        headerName: 'Diff (USD)',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        sortable: true,
        sort: 'desc',
        comparator: absComparator,
        valueFormatter: (params) => (params.value != null ? formatNum(params.value) : 'N/A'),
        cellClass: (params: CellClassParams) => `ag-right-cell ${getDiffUsdCellClass(params)}`.trim(),
        headerClass: 'ag-center-header'
    },
    {
        field: 'paymentDeltaPercentageUsd',
        headerName: 'Change % (USD)',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        sortable: true,
        comparator: absComparator,
        valueFormatter: (params) => formatPct(params.value, 'N/A'),
        cellClass: (params: CellClassParams) => `ag-right-cell ${getChangePercentCellClass(params)}`.trim(),
        headerClass: 'ag-center-header'
    }
];
